/**
 * /learn/courses — course library, three buckets via CourseBucketTabs.
 */

import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { AcademicCapIcon } from '@heroicons/react/24/outline';
import { slideUp } from '@/lib/motion';
import PageHeader from '@/components/common/PageHeader';
import { getCourses } from '../../api/me';
import { toDisplayBuckets } from '../../utils/courseBuckets';
import CourseBucketTabs from '../../components/learner/CourseBucketTabs';
import { learnerKeys } from './DashboardPage';

const CoursesPage: React.FC = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: learnerKeys.courses,
    queryFn: getCourses,
  });

  // Count from the reclassified display buckets — expired in-progress
  // enrolments count toward Finalized, not toward In Progress. The sum equals
  // the raw backend total; this just stays consistent with CourseBucketTabs.
  // Must sit above the early returns so hook order stays stable across the
  // loading → loaded transition.
  const display = useMemo(() => (data ? toDisplayBuckets(data) : null), [data]);

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-40 rounded bg-text-tertiary/10" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-56 rounded-2xl bg-text-tertiary/10" />
          ))}
        </div>
      </div>
    );
  }

  if (isError || !data || !display) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <p className="text-text-secondary">Failed to load courses. Please refresh.</p>
      </div>
    );
  }

  const totalCount =
    display.not_started.length + display.in_progress.length + display.finalized.length;

  if (totalCount === 0) {
    return (
      <motion.div {...slideUp} className="space-y-6">
        <PageHeader title="My Courses" root={{ label: 'Home', to: '/learn/dashboard' }} />
        <div className="card p-12 text-center">
          <div
            className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{
              background:
                'linear-gradient(135deg, rgb(var(--color-primary) / 0.18) 0%, rgb(var(--color-secondary) / 0.18) 100%)',
            }}
          >
            <AcademicCapIcon className="w-10 h-10 text-primary-500" />
          </div>
          <h3 className="text-base font-semibold text-text-primary mb-1">No Courses Yet</h3>
          <p className="text-sm text-text-secondary mb-4">
            Your team admin hasn't assigned any courses yet. Check back soon.
          </p>
          <Link
            to="/learn/dashboard"
            className="inline-flex items-center gap-1.5 text-sm font-bold text-primary-500 hover:underline"
          >
            ← Back to Dashboard
          </Link>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div {...slideUp} className="space-y-6">
      <header>
        <PageHeader title="My Courses" root={{ label: 'Home', to: '/learn/dashboard' }} />
        <p className="text-sm text-text-secondary mt-2">
          {totalCount} {totalCount === 1 ? 'course' : 'courses'} assigned to you.
        </p>
      </header>
      <CourseBucketTabs buckets={data} />
    </motion.div>
  );
};

export default CoursesPage;
